import { useEffect, useRef } from 'react';
import dayJs from 'dayjs';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useSettingsByPath } from 'pos-client/hooks/useSettings.tsx';
import { dayJsWeekdayAsString } from 'pos-client/utils/dateHelpers.ts';
import type {
  JobTicketDataFeModel,
  ScheduleBoardBlockedSlotModel,
  ScheduleBoardJobModel,
} from 'pos-shared/models/schedulingSchema.ts';

import { BlockedSlot } from './BlockedSlot.tsx';
import { JobSlot } from './JobSlot.tsx';

interface JobProps {
  data: JobTicketDataFeModel | any;
  userIsDragging: boolean;
}

export const Slot = ({ data, userIsDragging }: JobProps) => {
  // Handle both wrapped and unwrapped data formats
  const appointmentData = data?.appointmentData || data;
  const { selectedDate, selectedScheduleId, laneConflicts } =
    useScheduleBoardContext();
  const selectedDateDayJs = dayJs(selectedDate);
  const selectedDateWeekday = dayJsWeekdayAsString(selectedDateDayJs);
  const { first: defaultSetting } = useSettingsByPath({
    path: 'Features>EnableDefaultLanes',
  });

  const defaultEnabled = defaultSetting?.value === 'true';

  const isBlockedSlot =
    appointmentData?.text?.toLowerCase().includes('blocked') ?? false;

  const jobId = 'jobId' in appointmentData ? appointmentData.jobId : undefined;

  const isInConflictLane =
    !!jobId &&
    (laneConflicts ?? []).some(
      (conflict) =>
        conflict.laneId === appointmentData.laneId &&
        selectedDateDayJs.isSame(dayJs(conflict.date), 'day') &&
        conflict.conflicts.some(
          (item) =>
            !item.acknowledged &&
            (item.jobId === jobId || item.conflictingJobId === jobId)
        )
    );

  const targetChildRef = useRef<HTMLDivElement | null>(null);

  const lanesDefaultDays = appointmentData?.defaultDays ?? [];
  const isDefaultLane =
    defaultEnabled && lanesDefaultDays.includes(selectedDateWeekday);

  useEffect(() => {
    const parentElement = targetChildRef?.current?.parentElement;
    const appointmentElement = parentElement?.parentElement;
    const classList = appointmentElement?.classList;

    classList?.add(
      `slot-${appointmentData.state
        .toLowerCase()
        .replace(' ', '-')
        .replace(/ /g, '')}`
    );
    classList?.add('slot');

    if ('jobId' in appointmentData) {
      appointmentElement?.setAttribute(
        'data-testid',
        `slot-${appointmentData.jobId}`
      );
    }

    classList?.toggle(
      'slot-highlight',
      'scheduleId' in appointmentData &&
        selectedScheduleId === appointmentData.scheduleId
    );
    classList?.toggle('slot-conflict', isInConflictLane);
    classList?.toggle('slot-default-lane', isDefaultLane);
    classList?.toggle('slot-blocked', isBlockedSlot);
  }, [
    appointmentData,
    selectedScheduleId,
    isInConflictLane,
    isDefaultLane,
    isBlockedSlot,
  ]);

  return isBlockedSlot ? (
    <BlockedSlot
      slotData={appointmentData as ScheduleBoardBlockedSlotModel}
      targetChildRef={targetChildRef}
    />
  ) : (
    <JobSlot
      jobData={appointmentData as ScheduleBoardJobModel}
      targetChildRef={targetChildRef}
      isInConflict={isInConflictLane}
      isDefaultLane={isDefaultLane}
      userIsDragging={userIsDragging}
    />
  );
};
